import type z from '@/lib/zod';
import {
  LEGACY_TENANTS_SEARCH_KEYS,
  toLegacyTenantsTab,
  type LegacyTenantsSearchKey,
} from '@/pages/tenants/lib/tenants-filters';
import { commonSearchSchema } from './commonSearchSchema';
import { optionalSearchString } from './search-param-schemas';
import { tenantDetailSearchSchema } from './tenantDetailSearchSchema';

/**
 * I parametri dei link di prima (`LEGACY_TENANTS_SEARCH_KEYS`) restano nello
 * schema solo per diventare `subTab`: altrimenti all'API si sommerebbero
 * come filtri.
 */
const legacyTenantsSearchShape = Object.fromEntries(
  LEGACY_TENANTS_SEARCH_KEYS.map((key) => [key, optionalSearchString()])
) as Record<LegacyTenantsSearchKey, ReturnType<typeof optionalSearchString>>;

/**
 * Filtri della lista aziende: la stessa pagina di `/admin` e `/referrer`.
 * Il tab (stato del tenant o vista Statistiche) viaggia su `subTab`, come
 * nelle altre liste; ogni area ha lo schema con i suoi tab, così un tab
 * dell'admin scritto nell'URL del referrer non lascia la barra spenta.
 */
const baseTenantsSearchSchema = commonSearchSchema.extend({
  subTab: optionalSearchString(),
  hasCassettoFiscale: optionalSearchString(),
  planId: optionalSearchString(),
  referrerId: optionalSearchString(),
  /** Scheda del dettaglio aperto sopra la lista (`$id`). */
  ...tenantDetailSearchSchema.shape,
  ...legacyTenantsSearchShape,
});

const buildTenantsSearchSchema = (isAdminArea: boolean) =>
  baseTenantsSearchSchema.transform(
    ({ status, isActive, planStatus, tab, ...search }) => ({
      ...search,
      subTab: toLegacyTenantsTab({
        isAdminArea,
        subTab: search.subTab,
        status,
        isActive,
        planStatus,
        tab,
      }),
    })
  );

export const adminTenantsSearchSchema = buildTenantsSearchSchema(true);
export const referrerTenantsSearchSchema = buildTenantsSearchSchema(false);

export type TenantsSearch = z.output<typeof adminTenantsSearchSchema>;
